"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import Badge from "@mui/material/Badge";
import { styled } from "@mui/material/styles";
import { ShoppingCart, User, Camera, Heart } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { Box } from "@mui/material";
import { useSafeSelector } from "@package/util/hooks/useSafeSelector";
import { useDispatch } from "react-redux";
import { productSlug } from "@package/util/utils/productSlug";
import {
  sendEmailOtp,
  resendEmailOtp,
  verifyEmailOtp,
} from "@package/api/slices/EmailAuthSlice";
import {
  checkTokenExpiry,
  getGuestToken,
  getToken,
  removeGuestToken,
  saveGuestToken,
  saveToken,
} from "@package/util/token/token";
import { getTenantName } from "@package/util/domain/DomainHeader";
import { setIsOtpModalOpen } from "@package/api/slices/otpAuthSlice";
import { GetAllCartItem } from "@package/api/slices/product-processing-slices/CartSlice";
import { hydrateWishlist } from "@package/api/slices/product-processing-slices/WishlistSlice";
import { resolvePackageAssetUrl } from "@package/util/assetUrls";
import { identifyMetaPixelCustomer } from "@package/util/utils/metaPixel";
import CartPopupDrawer from "../../cart/components/CartPopupDrawer";
import EntHeader from "../../../agavian-ent/components/home/Header";
import { GetGeneralTokenApi } from "@package/api/api/custom-domain-api/CustomDomainApi";
const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
   right: 2,
    top: 3,
    border: `2px solid ${(theme.vars ?? theme).palette.background.paper}`,
    padding: "0 4px",
  },
}));

function buildTenantAssetUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;

  const baseUrl = process.env.NEXT_PUBLIC_SECURED_URL || "";
  if (!baseUrl) return path;

  return `${baseUrl.replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`;
}

function getGuestTokenFromResponse(response) {
  const data = response?.data;
  const responseData = data?.response ?? data?.data ?? data;

  if (typeof responseData === "string") return responseData;

  return (
    responseData?.guestToken ||
    responseData?.token ||
    responseData?.accessToken ||
    data?.guestToken ||
    data?.token ||
    ""
  );
}

function getNavbarThemeStyles(theme, defaults = {}) {
  const navbarTheme = theme?.navbar || {};

  const defaultOtpModalTheme = {
    modalBg: "#ffffff",
    heading: "#222222",
    text: "#444444",
    subtext: "#666666",
    border: "rgba(123, 80, 111, 0.18)",
    inputBg: "#ffffff",
    inputText: "#101828",
    primaryBtnBg: theme?.primaryColor || "#7b506f",
    primaryBtnText: "#ffffff",
    secondaryBtnBg: "#ffffff",
    secondaryBtnText: "#1f1f1f",
    divider: "rgba(0, 0, 0, 0.08)",
  };

  const defaultSearch = {
    wrapperBg: "#ffffff",
    wrapperBorder: `1px solid ${theme?.navBorderColor || "rgba(16, 24, 40, 0.12)"}`,
    inputColor: "#101828",
    inputPlaceholder: "#667085",
    iconBg: "transparent",
  };

  return {
    layout: navbarTheme.layout || "default",
    backgroundColor: navbarTheme.backgroundColor || defaults.backgroundColor || "#ffffff",
    backgroundImageAsset: navbarTheme.backgroundImageAsset || null,
    backgroundImage: navbarTheme.backgroundImage || null,
    textColor: navbarTheme.textColor || defaults.textColor || theme?.primaryColor || "#6b2b52",
    iconColor: navbarTheme.iconColor || theme?.primaryColor || "#6b2b52",
    logoColor: navbarTheme.logoColor || theme?.primaryColor || "#6b2b52",
    desktopPadding: navbarTheme.desktopPadding || "1rem 3rem",
    mobilePadding: navbarTheme.mobilePadding || "0.75rem 1rem",
    compactMobilePadding: navbarTheme.compactMobilePadding || "0.75rem 0.5rem",
    boxShadow: navbarTheme.boxShadow || "0 2px 4px rgba(0,0,0,0.05)",
    borderBottom: navbarTheme.borderBottom || `1px solid ${defaults.borderBottom || "#e8d4dc"}`,
    mobileMenuBackgroundColor: navbarTheme.mobileMenuBackgroundColor || "#ffffff",
    activeColor: navbarTheme.activeColor || null,
    hoverColor: navbarTheme.hoverColor || defaults.hoverColor || theme?.navHoverColor || "#8b3a6a",
    shopMenuAccentColor: navbarTheme.shopMenuAccentColor || null,
    search: {
      ...defaultSearch,
      ...(navbarTheme.search || {}),
      iconColor:
        navbarTheme.search?.iconColor ||
        defaultSearch.iconColor ||
        navbarTheme.iconColor ||
        theme?.primaryColor ||
        "#6b2b52",
    },
    otpModal: {
      ...defaultOtpModalTheme,
      ...(navbarTheme.otpModal || {}),
    },
    useFilledUserIcon: navbarTheme.useFilledUserIcon || false,
  };
}

function Spinner({ size = 16 }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: "2px solid rgba(255,255,255,0.45)",
        borderTopColor: "rgba(255,255,255,1)",
        display: "inline-block",
        animation: "spin 0.8s linear infinite",
      }}
    />
  );
}

export default function NavbarSection({
  config,
  theme,
  sticky = true,
  websiteDetails,
  isEcommerceEnabled,
  productMenuFilters = [],
  onProductsClick,
  companyDetails,
}) {
  const {
    logo,
    logoText,
    backgroundColor,
    textColor,
    logoTextColor,
    links,
    cameraLabel,
    loginLabel,
    hoverColor,
  } = config;

  const shouldShowServicesTab = companyDetails?.enableService === true;
  const heartIcon = resolvePackageAssetUrl("/assets/icons/heartIcon9.svg");
  const cartIcon = resolvePackageAssetUrl("/assets/icons/cartIcon.svg");
  const userIcon = resolvePackageAssetUrl("/assets/icons/userIcon.svg");
  const navbarLogoImage = resolvePackageAssetUrl("/assets/images/navbarLogo.png");
  const googleIcon = resolvePackageAssetUrl("/assets/images/googleicon.png");
  const filteredLinks = Array.isArray(links)
    ? links.filter((link) => {
        const href = String(link?.href || "").toLowerCase();
        const label = String(link?.label || "").toLowerCase();
        const isServicesLink =
          href === "/services" ||
          href === "/services/" ||
          label === "services" ||
          label === "service";

        if (isServicesLink) return shouldShowServicesTab;
        return true;
      })
    : [];

  const pathname = usePathname();
  const router = useRouter();
  const navbarTheme = getNavbarThemeStyles(theme, {
    backgroundColor,
    textColor,
    hoverColor,
    borderBottom: config.borderBottom,
  });
  const shouldShowCameraNav =
    theme?.variant !== "tiny_tusker" && navbarTheme.layout !== "tiny";
  const navbarBackgroundImage =
    navbarTheme.backgroundImage ||
    (navbarTheme.backgroundImageAsset
      ? `url('${resolvePackageAssetUrl(navbarTheme.backgroundImageAsset)}')`
      : "none");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isCompactMobile, setIsCompactMobile] = useState(false);
  const [currentHash, setCurrentHash] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isShopMenuOpen, setIsShopMenuOpen] = useState(false);
  const [showMobileCategories, setShowMobileCategories] = useState(false);
  const [mobileOpenDropdownKey, setMobileOpenDropdownKey] = useState(null);
  const [isShopMenuPinned, setIsShopMenuPinned] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCartPopupOpen, setIsCartPopupOpen] = useState(false);
  const [hasGuestToken, setHasGuestToken] = useState(false);
  const searchInputRef = useRef(null);
const shopMenuCloseTimerRef = useRef(null);
const guestTokenRequestStartedRef = useRef(false);

  const normalizePath = (value = "") => {
    const stringValue = String(value || "");
    const [pathWithQuery = "", hash = ""] = stringValue.split("#");
    const [pathOnly] = pathWithQuery.split("?");
    const normalizedPath =
      !pathOnly || pathOnly === ""
        ? "/"
        : pathOnly.length > 1 && pathOnly.endsWith("/")
          ? pathOnly.slice(0, -1)
          : pathOnly;

    return hash ? `${normalizedPath}#${hash}` : normalizedPath;
  };

  const isLinkActive = (href = "") =>
    normalizePath(href) === normalizePath(pathname);
  const resolvedBrandLogo =
    buildTenantAssetUrl(websiteDetails?.websiteLogoUrl) ||
    logo ||
    navbarLogoImage;
  const brandTitle = websiteDetails?.websiteTitle || logoText || "Padami";
const normalizeAgeLabel = (item, index) => {
    if (!item) {
      return `Age ${index + 1}`;
    }

    const rawLabel =
      item?.label ||
      item?.name ||
      item?.value ||
      item?.title ||
      item?.ageRange ||
      item?.age_range ||
      "";

    const trimmedLabel =
      typeof rawLabel === "string" ? rawLabel.trim() : String(rawLabel || "");

    if (trimmedLabel) {
      return /^age\b/i.test(trimmedLabel)
        ? trimmedLabel
        : `Age ${trimmedLabel}`;
    }

    const minAge = item?.minAge ?? item?.min ?? item?.fromAge ?? item?.from;
    const maxAge = item?.maxAge ?? item?.max ?? item?.toAge ?? item?.to;

    if (
      minAge !== undefined &&
      minAge !== null &&
      maxAge !== undefined &&
      maxAge !== null
    ) {
      return `Age ${minAge} - ${maxAge}`;
    }

    if (minAge !== undefined && minAge !== null) {
      return `Age ${minAge}+`;
    }

    return `Age ${index + 1}`;
  };

  const normalizeFilterLabel = (item, index, fallbackPrefix = "Item") => {
    const rawLabel =
      item?.label ||
      item?.categoryName ||
      item?.name ||
      item?.value ||
      item?.title ||
      "";

    const trimmedLabel =
      typeof rawLabel === "string" ? rawLabel.trim() : String(rawLabel || "");

    return trimmedLabel || `${fallbackPrefix} ${index + 1}`;
  };

  const normalizedProductMenuGroups = Array.isArray(productMenuFilters)
    ? productMenuFilters
        .map((group, groupIndex) => {
          const ageItems = Array.isArray(group?.ageFilters)
            ? group.ageFilters.map((item, itemIndex) => ({
                id:
                  item?.id ??
                  item?.ageFilterId ??
                  item?.filterId ??
                  item?.websiteFilterId ??
                  item?.filterValueId ??
                  item?.websiteFilterValueId ??
                  `${group?.filterHeader || "age"}-${itemIndex}`,
                label: normalizeAgeLabel(item, itemIndex),
                type: "age",
              }))
            : [];

          const categoryItems = Array.isArray(group?.categoryFilters)
            ? group.categoryFilters.map((item, itemIndex) => ({
                id:
                  item?.id ??
                  item?.categoryId ??
                  item?.filterId ??
                  item?.websiteFilterId ??
                  item?.filterValueId ??
                  item?.websiteFilterValueId ??
                  `${group?.filterHeader || "category"}-${itemIndex}`,
                label: normalizeFilterLabel(item, itemIndex, "Category"),
                type: "category",
              }))
            : [];

          const customItems = Array.isArray(group?.customFilters)
            ? group.customFilters.map((item, itemIndex) => ({
                id:
                  item?.id ??
                  item?.customFilterId ??
                  item?.filterId ??
                  item?.websiteFilterId ??
                  item?.filterValueId ??
                  item?.websiteFilterValueId ??
                  `${group?.filterHeader || "custom"}-${itemIndex}`,
                label: normalizeFilterLabel(item, itemIndex, "Filter"),
                type: "custom",
              }))
            : [];

          const items = [...ageItems, ...categoryItems, ...customItems].filter(
            (item) => item?.label,
          );

          return {
            id: group?.id ?? group?.filterHeader ?? `group-${groupIndex}`,
            filterHeader: group?.filterHeader || `Shop ${groupIndex + 1}`,
            items,
          };
        })
        .filter((group) => group.items.length > 0)
    : [];

  const shouldShowShopMenu = normalizedProductMenuGroups.length > 0;

  const isShopTriggerLink = (link) => {
    const href = String(link?.href || "").toLowerCase();
    const label = String(link?.label || "").toLowerCase();

    return (
      href === "/products" ||
      label.includes("product") ||
      label.includes("category") ||
      label.includes("categories")
    );
  };

  const clearShopMenuCloseTimer = () => {
    if (shopMenuCloseTimerRef.current) {
      window.clearTimeout(shopMenuCloseTimerRef.current);
      shopMenuCloseTimerRef.current = null;
    }
  };

  const openShopMenu = () => {
    clearShopMenuCloseTimer();
    setIsShopMenuOpen(true);
  };

  const scheduleShopMenuClose = () => {
    if (isShopMenuPinned) {
      return;
    }

    clearShopMenuCloseTimer();
    shopMenuCloseTimerRef.current = window.setTimeout(() => {
      setIsShopMenuOpen(false);
    }, 120);
  };

  const closeShopMenu = () => {
    clearShopMenuCloseTimer();
    setIsShopMenuPinned(false);
    setIsShopMenuOpen(false);
  };

  const getMegaMenuItemHref = (item) => {
    if (item?.type === "age") {
      return `/products?age=${encodeURIComponent(item.label)}`;
    }

    if (item?.type === "category") {
      const categorySlug = productSlug(item.label);
      return `/products?category=${encodeURIComponent(categorySlug)}&id=${encodeURIComponent(item.id)}`;
    }

    return `/products?search=${encodeURIComponent(item.label)}`;
  };

  const handleMegaMenuItemClick = (item) => {
    closeShopMenu();
    setIsMobileMenuOpen(false);
    router.push(getMegaMenuItemHref(item));
  };

  const handleOpenCartPopup = () => {
    setIsMobileMenuOpen(false);
    setIsCartPopupOpen(true);
  };

  const renderShopMegaMenu = (options = {}) => {
    const {
      isTinyVariant = false,
      accentColor =
        navbarTheme.shopMenuAccentColor || activeNavUnderlineColor,
      headingFontFamily = theme.navLinkFontFamily || theme.fontFamily,
      itemFontFamily = theme.navLinkFontFamily || theme.fontFamily,
    } = options;
    const groupCount = normalizedProductMenuGroups.length || 1;
    const longestGroupLength = normalizedProductMenuGroups.reduce(
      (max, group) => Math.max(max, group?.items?.length || 0),
      0,
    );
    const preferredColumnWidth =
      longestGroupLength <= 4 ? 170 : longestGroupLength <= 8 ? 200 : 230;
    const maxPopupWidth = isTinyVariant ? 1180 : 1240;
    const minPopupWidth = isTinyVariant ? 320 : 360;
    const estimatedWidth = Math.min(
      maxPopupWidth,
      Math.max(minPopupWidth, groupCount * preferredColumnWidth + (groupCount - 1) * 28 + 72),
    );

    return (
      <div
        data-shop-mega-menu="true"
        onMouseEnter={openShopMenu}
        onMouseLeave={scheduleShopMenuClose}
        style={{
          position: "absolute",
          top: isTinyVariant ? "100%" : "calc(100% + 0.9rem)",
          left: "50%",
          transform: "translateX(-50%)",
          width: `min(${estimatedWidth}px, ${isTinyVariant ? "calc(100vw - 2rem)" : "calc(100vw - 3rem)"})`,
          backgroundColor: "#ffffff",
          borderTop: isTinyVariant
            ? "1px solid rgba(20, 66, 89, 0.08)"
            : "none",
          borderRadius: "0 0 24px 24px",
          boxShadow: isTinyVariant
            ? "0 24px 48px rgba(20, 66, 89, 0.14)"
            : "0 24px 48px rgba(16, 24, 40, 0.14)",
          padding: "2rem 2.25rem 2.15rem",
          zIndex: 1002,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${groupCount}, minmax(0, 1fr))`,
            gap: "2rem 1.75rem",
            alignItems: "start",
          }}
        >
          {normalizedProductMenuGroups.map((group) => (
            <div key={group.id}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.7rem",
                  marginBottom: "1rem",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: "7px",
                    height: "7px",
                    backgroundColor: accentColor,
                    display: "inline-block",
                  }}
                />
                <span
                  style={{
                    color: "#202428",
                    fontFamily: headingFontFamily,
                    fontSize: isTinyVariant ? "1.08rem" : "1rem",
                    fontWeight: 800,
                    lineHeight: 1.2,
                    textTransform: "uppercase",
                  }}
                >
                  {group.filterHeader}
                </span>
              </div>

              <div
                style={{
                  display: "grid",
                  gap: "0.72rem",
                  justifyItems: "start",
                }}
              >
                {group.items.map((item) => (
                  <button
                    key={`${group.id}-${item.id}-${item.label}`}
                    type="button"
                    onClick={() => handleMegaMenuItemClick(item)}
                    style={{
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      color: "#2c3136",
                      fontFamily: itemFontFamily,
                      fontSize: isTinyVariant ? "1rem" : "0.96rem",
                      fontWeight: 500,
                      lineHeight: 1.35,
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  // Tenant
  const [tenantName, setTenantName] = useState("");
  const [tenantLoading, setTenantLoading] = useState(true);

  // OTP state
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isEmailLoginExpanded, setIsEmailLoginExpanded] = useState(false);

  const [otpButtonLoading, setIsOtpButtonLoading] = useState(false);
  const [otpResendButtonLoading, setOtpResendButtonLoading] = useState(false);
  const [otpVerifyButtonLoading, setOtpVerifyButtonLoading] = useState(false);

  const [countdown, setCountdown] = useState(60);
  const [otpMessage, setOtpMessage] = useState("");

  const dispatch = useDispatch();

  const iconColorToUse = navbarTheme.iconColor;
  const navTextColor = navbarTheme.textColor;

  const activeNavUnderlineColor =
    navbarTheme.activeColor ||
    theme.navActiveColor ||
    theme.accentColor ||
    theme.secondaryColor ||
    theme.primaryColor ||
    hoverColor ||
    navTextColor;

  const logoColor = navbarTheme.logoColor;
  const otpModalTheme = navbarTheme.otpModal;

  const { UserSessionPayload = {}, isOtpModalOpen = false } = useSafeSelector(
    (state) => state.auth,
    {},
  );
  const metaPixelId = companyDetails?.companyAnalytic?.metaPixelId || "";
  const { cartItems = {} } = useSafeSelector((state) => state.cart, {});
  const { itemsById: wishlistItemsById = {} } = useSafeSelector(
    (state) => state.wishlist,
    {},
  );
  const cartItemCount = Array.isArray(cartItems?.items)
    ? cartItems.items.reduce((total, item) => {
        const quantity = Number(item?.quantity);
        return total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 1);
      }, 0)
    : 0;
  const wishlistItemCount = Object.keys(wishlistItemsById || {}).length;
  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const isLoggedIn = !UserSessionPayload?.isTokenExpired;
  const isGuestCartDisabled = companyDetails?.guestCartDisable === true;
  const isGuestCartEnabled = companyDetails?.guestCartDisable === false;
  const canUseCart =
    isEcommerceEnabled && (isLoggedIn || (isGuestCartEnabled && hasGuestToken));

  // Load tenant once
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        if (typeof window === "undefined") return;
        const tn = await getTenantName(window.location.hostname);
        if (mounted) setTenantName(tn || "");
      } catch (e) {
        if (mounted) setTenantName("");
      } finally {
        if (mounted) setTenantLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // Mobile detection
  useEffect(() => {
    checkTokenExpiry();
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 1024);
      setIsCompactMobile(width < 366);
      if (width >= 1024) setIsMobileMenuOpen(false);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const syncHash = () => {
      setCurrentHash(window.location.hash || "");
    };

    syncHash();
    window.addEventListener("hashchange", syncHash);

    return () => window.removeEventListener("hashchange", syncHash);
  }, [pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (isGuestCartDisabled) {
      removeGuestToken();
      setHasGuestToken(false);
      window.dispatchEvent(new Event("guestTokenChanged"));
      return;
    }

    const syncGuestToken = () => {
      setHasGuestToken(Boolean(getGuestToken()));
    };

    syncGuestToken();
    window.addEventListener("guestTokenChanged", syncGuestToken);

    return () => {
      window.removeEventListener("guestTokenChanged", syncGuestToken);
    };
  }, [isGuestCartDisabled]);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !isEcommerceEnabled ||
      !isGuestCartEnabled ||
      isLoggedIn ||
      hasGuestToken ||
      guestTokenRequestStartedRef.current
    ) {
      return;
    }

    if (getToken("token")) {
      return;
    }

    const existingGuestToken = getGuestToken();
    if (existingGuestToken) {
      setHasGuestToken(true);
      return;
    }

    guestTokenRequestStartedRef.current = true;

    GetGeneralTokenApi()
      .then((response) => {
        const guestToken = getGuestTokenFromResponse(response);
        if (guestToken) {
          saveGuestToken(guestToken);
          setHasGuestToken(true);
          window.dispatchEvent(new Event("guestTokenChanged"));
        }
      })
      .catch((error) => {
        guestTokenRequestStartedRef.current = false;
        console.log("Error in navbar guest token fetch:", error);
      });
  }, [hasGuestToken, isEcommerceEnabled, isGuestCartEnabled, isLoggedIn]);

useEffect(() => {
    if (!canUseCart) {
      return;
    }

    dispatch(GetAllCartItem({}));
    if (isLoggedIn) {
      dispatch(hydrateWishlist());
    }
  }, [canUseCart, dispatch, isLoggedIn]);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      setShowMobileCategories(false);
      setMobileOpenDropdownKey(null);
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    return () => {
      if (shopMenuCloseTimerRef.current) {
        window.clearTimeout(shopMenuCloseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isShopMenuOpen) {
      return;
    }

    const handlePointerDown = (event) => {
      const triggerContainer = event.target?.closest?.("[data-shop-menu-root]");
      const popupContainer = event.target?.closest?.("[data-shop-mega-menu]");

      if (!triggerContainer && !popupContainer) {
        clearShopMenuCloseTimer();
        setIsShopMenuPinned(false);
        setIsShopMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [isShopMenuOpen, isShopMenuPinned]);
  // OTP countdown
  useEffect(() => {
    if (!isOtpModalOpen || !isOtpSent || countdown <= 0) return;
    const timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [isOtpModalOpen, isOtpSent, countdown]);

  const handleOpenOtpModal = () => {
    dispatch(setIsOtpModalOpen(true));
    setOtpMessage("");
    setIsEmailLoginExpanded(false);
  };

  const handleCloseOtpModal = () => {
    dispatch(setIsOtpModalOpen(false));
    setEmail("");
    setOtp("");
    setIsOtpSent(false);
    setIsEmailLoginExpanded(false);
    setCountdown(60);
    setOtpMessage("");
    setIsOtpButtonLoading(false);
    setOtpResendButtonLoading(false);
    setOtpVerifyButtonLoading(false);
  };

  // Google login (only if companyDetails exists)
  const handleGoogleLogin = () => {
    if (!companyDetails?.tenantCode) return;

    const tenant = companyDetails.tenantCode;
    if (metaPixelId && typeof window !== "undefined") {
      window.sessionStorage.setItem("metaPixelId", String(metaPixelId));
    }

    // where the user should go AFTER we store token
    const returnTo = window.location.pathname + window.location.search;

    // backend will redirect to this URL (frontend handler), and we keep returnTo inside it
    const redirectUri = `${window.location.origin}/oauth2/redirect?redirect=${encodeURIComponent(
      returnTo,
    )}`;

    const url =
      process.env.NEXT_PUBLIC_API_URL +
      `/auth/oauth2/authorize/google?` +
      new URLSearchParams({
        tenant,
        redirect_uri: redirectUri,
      }).toString();

    window.location.assign(url);
  };

  const handleSendOtp = async () => {
    if (!email || !isValidEmail(email)) {
      setOtpMessage("Please enter a valid email address.");
      return;
    }
    if (!tenantName) {
      setOtpMessage("Tenant not detected. Please refresh and try again.");
      return;
    }

    setIsOtpButtonLoading(true);
    setOtpMessage("");

    try {
      const res = await dispatch(
        sendEmailOtp({ email, role: "USER", tenantDomain: tenantName }),
      );

      if (res?.error) {
        setOtpMessage("Failed to send OTP. Please try again.");
        return;
      }

      setIsOtpSent(true);
      setCountdown(60);
      setOtp("");
      setOtpMessage("OTP sent successfully to your email.");
    } catch (e) {
      setOtpMessage("Failed to send OTP. Please try again.");
    } finally {
      setIsOtpButtonLoading(false);
    }
  };

  const handleReSendOtp = async () => {
    if (!email || !isValidEmail(email)) {
      setOtpMessage("Please enter a valid email address.");
      return;
    }
    if (!tenantName) {
      setOtpMessage("Tenant not detected. Please refresh and try again.");
      return;
    }

    setOtpResendButtonLoading(true);
    setOtpMessage("");

    try {
      const res = await dispatch(
        resendEmailOtp({ email, role: "USER", tenantDomain: tenantName }),
      );

      if (res?.error) {
        setOtpMessage("Failed to resend OTP. Please try again.");
        return;
      }

      setIsOtpSent(true);
      setCountdown(60);
      setOtp("");
      setOtpMessage("OTP resent successfully to your email.");
    } catch (e) {
      setOtpMessage("Failed to resend OTP. Please try again.");
    } finally {
      setOtpResendButtonLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 4) {
      setOtpMessage("Please enter a valid OTP.");
      return;
    }
    if (!tenantName) {
      setOtpMessage("Tenant not detected. Please refresh and try again.");
      return;
    }

    setOtpVerifyButtonLoading(true);
    setOtpMessage("");

    try {
      const response = await dispatch(
        verifyEmailOtp({ email, otp, tenantDomain: tenantName, role: "USER" }),
      );

      if (response?.payload?.data?.success === true) {
        saveToken({
          token: response?.payload?.data?.response?.accessToken || "",
          refreshToken: response?.payload?.data?.response?.refreshToken || "",
          email,
        });
        identifyMetaPixelCustomer(metaPixelId, { email });

        setOtpMessage("OTP verified successfully.");
        if (typeof window !== "undefined") {
          const returnTo = window.location.pathname + window.location.search;
          window.location.href = returnTo;
          return;
        }
      }

      setOtpMessage(
        response?.payload?.data?.message || "Invalid OTP. Please try again.",
      );
    } catch (e) {
      setOtpMessage("Verification failed. Please try again.");
    } finally {
      setOtpVerifyButtonLoading(false);
    }
  };

  const otpSendOrResendLoading = otpButtonLoading || otpResendButtonLoading;
  const searchPlaceholder =
    companyDetails?.searchPlaceholder || "Search products";
  const submitSearch = () => {
    const normalizedQuery = searchQuery.trim();

    if (!normalizedQuery) {
      router.push("/products");
      setIsMobileMenuOpen(false);
      setIsSearchOpen(false);
      return;
    }

    router.push(`/products?search=${encodeURIComponent(normalizedQuery)}`);
    setIsMobileMenuOpen(false);
  };

  const handleSearchSubmit = (event) => {
    event?.preventDefault?.();
    submitSearch();
  };

  const handleSearchIconClick = () => {
    if (!isSearchOpen) {
      setIsSearchOpen(true);
      return;
    }

    submitSearch();
  };

  useEffect(() => {
    if (!isSearchOpen) {
      return;
    }

    const focusTimer = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 120);

    return () => window.clearTimeout(focusTimer);
  }, [isSearchOpen]);

  const searchInputStyles = navbarTheme.search;

  const isEnterpriseNavbar =
    theme?.variant === "enterprise" || theme?.themeVariant === "enterprise";

  return (
    <>
      {isEnterpriseNavbar ? (
        <EntHeader
          isAuthenticated={!UserSessionPayload?.isTokenExpired}
          onAccountClick={() => {
            if (UserSessionPayload?.isTokenExpired) {
              handleOpenOtpModal();
              return;
            }

            router.push("/user/account");
          }}
        />
      ) : (
        <nav
          style={{
            backgroundColor: navbarTheme.backgroundColor,
            backgroundImage: navbarBackgroundImage,
            backgroundSize: "cover",
            backgroundPosition: "center",
            color: navTextColor,
            padding: isMobile
              ? isCompactMobile
                ? navbarTheme.compactMobilePadding
                : navbarTheme.mobilePadding
              : navbarTheme.desktopPadding,
            position: sticky ? "sticky" : "relative",
            top: sticky ? 0 : "auto",
            zIndex: 1000,
            boxShadow: navbarTheme.boxShadow,
            borderBottom: navbarTheme.borderBottom,
          }}
        >
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: isMobile ? (isCompactMobile ? "0.5rem" : "1rem") : "2rem",
        }}
      >
        {/* Logo Section */}
        <Link
          href="/"
          style={{
            display: "flex",
            alignItems: "center",
            gap: isCompactMobile ? "0.35rem" : "0.5rem",
            textDecoration: "none",
            minWidth: 0,
            flex: "1 1 auto",
          }}
        >
          <Image
            src={
              websiteDetails?.websiteLogoUrl
                ? process.env.NEXT_PUBLIC_SECURED_URL +
                  websiteDetails?.websiteLogoUrl
                : logo
            }
            alt="Logo"
            width={isCompactMobile ? 36 : isMobile ? 42 : 54}
            height={isCompactMobile ? 36 : isMobile ? 42 : 54}
            style={{ objectFit: "contain" }}
          />
          <span
            style={{
              display: "block",
              flex: "1 1 auto",
              minWidth: 0,
              fontFamily: theme.logoTextFontFamily || "Elsie Swash Caps, serif",
              fontWeight: theme.logoTextFontWeight || "900",
              fontSize: isMobile
                ? isCompactMobile
                  ? "18px"
                  : "24px"
                : "35px",
              lineHeight: isMobile ? "1.1" : "100%",
              color: logoColor,
              whiteSpace: isMobile ? "normal" : "nowrap",
              overflow: isMobile ? "visible" : "hidden",
              textOverflow: isMobile ? "clip" : "ellipsis",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {websiteDetails?.websiteTitle
              ? websiteDetails?.websiteTitle
              : logoText}
          </span>
        </Link>

        {/* Desktop Navigation Links */}
        {!isMobile && (
          <div style={{ display: "flex", gap: "2rem", alignItems: "center" }}>
            {filteredLinks.map((link, index) => {
              const isShopLink =
                shouldShowShopMenu && isShopTriggerLink(link);

              return (
                <div
                  key={index}
                  data-shop-menu-root={isShopLink ? "true" : undefined}
                  style={{ position: "relative" }}
                  onMouseEnter={isShopLink ? openShopMenu : undefined}
                  onMouseLeave={isShopLink ? scheduleShopMenuClose : undefined}
                >
                  {isShopLink ? (
                    <Link
                      href={link.href || "/products"}
                      className="nav-link"
                      aria-expanded={isShopMenuOpen}
                      aria-haspopup="menu"
                      onClick={() => {
                        closeShopMenu();
                        setIsMobileMenuOpen(false);
                      }}
                      style={{
                        color:
                          isLinkActive(link.href) || isShopMenuOpen
                            ? activeNavUnderlineColor
                            : navTextColor,
                        textDecoration: "none",
                        fontFamily: theme.navLinkFontFamily || theme.fontFamily,
                        fontWeight: theme.navLinkFontWeight || "700",
                        fontSize: "14px",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                        position: "relative",
                        padding: "0 0 0.35rem",
                        borderTop: "none",
                        borderLeft: "none",
                        borderRight: "none",
                        borderBottom:
                          isLinkActive(link.href) || isShopMenuOpen
                            ? `2px solid ${activeNavUnderlineColor}`
                            : "2px solid transparent",
                        transition: "color 0.3s ease, border-color 0.3s ease",
                        background: "transparent",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.38rem",
                      }}
                    >
                      <span>{link.label}</span>
                      <span
                        aria-hidden="true"
                        style={{
                          display: "inline-flex",
                          fontSize: "11px",
                          transform: isShopMenuOpen
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                        }}
                      >
                        ▾
                      </span>
                    </Link>
                  ) : (
                    <Link
                      href={link.href}
                      className="nav-link"
                      onClick={() => {
                        closeShopMenu();
                        setIsMobileMenuOpen(false);
                      }}
                      style={{
                        color: isLinkActive(link.href)
                          ? activeNavUnderlineColor
                          : navTextColor,
                        textDecoration: "none",
                        fontFamily: theme.navLinkFontFamily || theme.fontFamily,
                        fontWeight: theme.navLinkFontWeight || "700",
                        fontSize: "14px",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                        position: "relative",
                        paddingBottom: "0.35rem",
                        borderBottom: isLinkActive(link.href)
                          ? `2px solid ${activeNavUnderlineColor}`
                          : "2px solid transparent",
                        transition: "color 0.3s ease, border-color 0.3s ease",
                      }}
                    >
                      {link.label}
                    </Link>
                  )}

                  {isShopLink && isShopMenuOpen
                    ? renderShopMegaMenu()
                    : null}
                </div>
              );
            })}
          </div>
        )}

        {/* Right Side Icons & Mobile Toggle */}
        <div
          style={{
            display: "flex",
            gap: isMobile ? (isCompactMobile ? "0.6rem" : "1.2rem") : "2rem",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <form
            onSubmit={handleSearchSubmit}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: isSearchOpen ? "0.55rem" : "0",
              minWidth: isSearchOpen ? (isMobile ? "150px" : "230px") : "24px",
              maxWidth: isSearchOpen ? (isMobile ? "170px" : "250px") : "24px",
              transition: "min-width 0.25s ease, max-width 0.25s ease, gap 0.25s ease",
              overflow: "hidden",
            }}
          >
            <input
              ref={searchInputRef}
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onBlur={() => {
                if (!searchQuery.trim()) {
                  setIsSearchOpen(false);
                }
              }}
              placeholder={searchPlaceholder}
              style={{
                width: isSearchOpen ? "100%" : "0px",
                minWidth: isSearchOpen ? "0" : "0",
                opacity: isSearchOpen ? 1 : 0,
                pointerEvents: isSearchOpen ? "auto" : "none",
                border: isSearchOpen ? searchInputStyles.wrapperBorder : "none",
                outline: "none",
                background: searchInputStyles.wrapperBg,
                color: searchInputStyles.inputColor,
                borderRadius: "999px",
                padding: isSearchOpen ? "0.5rem 0.8rem" : "0",
                fontFamily: theme.navLinkFontFamily || theme.fontFamily,
                fontSize: "14px",
                fontWeight: 500,
                transition: "width 0.25s ease, opacity 0.2s ease, padding 0.25s ease",
              }}
            />
            <button
              type="button"
              onClick={handleSearchIconClick}
              aria-label="Search products"
              style={{
                border: "none",
                background: searchInputStyles.iconBg,
                color: searchInputStyles.iconColor,
                padding: 0,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg
                width={isMobile ? "22" : "24"}
                height={isMobile ? "22" : "24"}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </button>
          </form>

          {shouldShowCameraNav &&
            companyDetails?.tenantCode !== "TENT-2FE94B5A" &&
            tenantName !== "TENT-2FE94B5A" && (
              <Link
                href="/armagic"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textDecoration: "none",
                  gap: "4px",
                }}
              >
                <span style={{ color: iconColorToUse, display: "flex" }}>
                  <Camera
                    size={isCompactMobile ? 18 : isMobile ? 20 : 28}
                    strokeWidth={2}
                  />
                </span>
                {!isMobile && (
                  <span
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: navTextColor,
                    }}
                  >
                    {cameraLabel}
                  </span>
                )}
              </Link>
            )}

          {isEcommerceEnabled && isLoggedIn ? (
            <Link
              href="/wishlist"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textDecoration: "none",
                gap: "4px",
              }}
            >
              <span style={{ color: iconColorToUse, display: "flex" }}>
                <StyledBadge badgeContent={wishlistItemCount} color="primary">
                  <Heart size={isMobile ? 22 : 28} strokeWidth={2} />
                </StyledBadge>
              </span>
              {!isMobile && (
                <span
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: navTextColor,
                  }}
                >
                  WISHLIST
                </span>
              )}
            </Link>
          ) : null}

          {canUseCart ? (
            <button
              type="button"
              onClick={handleOpenCartPopup}
              aria-label="Open cart"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "4px",
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              <span style={{ color: iconColorToUse, display: "flex" }}>
                <StyledBadge badgeContent={cartItemCount} color="primary">
                  <ShoppingCart size={isMobile ? 22 : 28} strokeWidth={2} />
                </StyledBadge>
              </span>
              {!isMobile && (
                <span
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: navTextColor,
                  }}
                >
                  CART
                </span>
              )}
            </button>
          ) : null}

          {isEcommerceEnabled && !isLoggedIn ? (
            <button
              type="button"
              onClick={handleOpenOtpModal}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "4px",
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              {navbarTheme.useFilledUserIcon ? (
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    backgroundColor: iconColorToUse,
                    border: "2px solid #fff",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <User size={18} color="#fff" />
                </div>
              ) : (
                <span style={{ color: iconColorToUse, display: "flex" }}>
                  <User
                    size={isCompactMobile ? 18 : isMobile ? 20 : 28}
                    strokeWidth={2}
                  />
                </span>
              )}
              {!isMobile && (
                <span
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: navTextColor,
                  }}
                >
                  {loginLabel}
                </span>
              )}
            </button>
          ) : null}

          {!isMobile &&
          isLoggedIn &&
          isEcommerceEnabled ? (
            <Link
              href="/user/account"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textDecoration: "none",
                gap: "4px",
              }}
            >
              {navbarTheme.useFilledUserIcon ? (
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    backgroundColor: iconColorToUse,
                    border: "2px solid #fff",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <User size={18} color="#fff" />
                </div>
              ) : (
                <span style={{ color: iconColorToUse, display: "flex" }}>
                  <User
                    size={isCompactMobile ? 18 : isMobile ? 20 : 28}
                    strokeWidth={2}
                  />
                </span>
              )}
              {!isMobile && (
                <span
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: navTextColor,
                  }}
                >
                  ACCOUNT
                </span>
              )}
            </Link>
          ) : null}

          {isMobile && (
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{
                background: "none",
                border: "none",
                padding: isCompactMobile ? "2px" : "5px",
                cursor: "pointer",
                color: navTextColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              aria-label="Toggle menu"
            >
              <svg
                width={isCompactMobile ? "20" : "24"}
                height={isCompactMobile ? "20" : "24"}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                {isMobileMenuOpen ? (
                  <path d="M18 6L6 18M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMobile && isMobileMenuOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor:
              navbarTheme.mobileMenuBackgroundColor,
            padding: "1rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            borderTop: "1px solid rgba(0,0,0,0.05)",
            zIndex: 999,
          }}
        >
          {filteredLinks.map((link, index) => {
            const isShopLink = shouldShowShopMenu && isShopTriggerLink(link);

            if (isShopLink) {
              return (
                <div key={index}>
                  <button
                    type="button"
                    aria-expanded={showMobileCategories}
                    aria-controls="mobile-product-categories"
                    onClick={() =>
                      setShowMobileCategories((isVisible) => !isVisible)
                    }
                    style={{
                      width: "100%",
                      color:
                        isLinkActive(link.href) || showMobileCategories
                          ? activeNavUnderlineColor
                          : navTextColor,
                      background: "transparent",
                      border: "none",
                      borderBottom:
                        isLinkActive(link.href) || showMobileCategories
                          ? `2px solid ${activeNavUnderlineColor}`
                          : "2px solid transparent",
                      textDecoration: "none",
                      fontWeight: "700",
                      fontSize: "16px",
                      padding: "0.5rem 0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <span>{link.label}</span>
                    <span
                      aria-hidden="true"
                      style={{
                        fontSize: "12px",
                        transform: showMobileCategories
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    >
                      ▾
                    </span>
                  </button>

                  {showMobileCategories ? (
                    <div
                      id="mobile-product-categories"
                      style={{
                        display: "grid",
                        gap: "0.85rem",
                        padding: "0.85rem 0 0.2rem 0.75rem",
                      }}
                    >
                      <Link
                        href={link.href || "/products"}
                        style={{
                          color: activeNavUnderlineColor,
                          textDecoration: "none",
                          fontWeight: "800",
                          fontSize: "15px",
                          padding: "0.2rem 0",
                        }}
                      >
                        All products
                      </Link>

                      {normalizedProductMenuGroups.map((group) => (
                        <div
                          key={group.id}
                          style={{
                            display: "grid",
                            gap: "0.55rem",
                          }}
                        >
                          <span
                            style={{
                              color: navTextColor,
                              fontSize: "13px",
                              fontWeight: 800,
                              textTransform: "uppercase",
                              opacity: 0.75,
                            }}
                          >
                            {group.filterHeader}
                          </span>

                          {group.items.map((item) => (
                            <Link
                              key={`${group.id}-${item.id}-${item.label}`}
                              href={getMegaMenuItemHref(item)}
                              style={{
                                color: navTextColor,
                                textDecoration: "none",
                                fontSize: "15px",
                                fontWeight: 650,
                                padding: "0.1rem 0",
                                textAlign: "left",
                              }}
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            }

            if (Array.isArray(link.dropdown) && link.dropdown.length > 0) {
              const dropdownKey = `${link.label}-${index}`;
              const isDropdownOpen = mobileOpenDropdownKey === dropdownKey;
              const isDropdownActive =
                isLinkActive(link.href) ||
                link.dropdown.some((dropdownItem) =>
                  isLinkActive(dropdownItem.href)
                );

              return (
                <div key={dropdownKey}>
                  <button
                    type="button"
                    aria-expanded={isDropdownOpen}
                    aria-controls={`mobile-dropdown-${index}`}
                    onClick={() =>
                      setMobileOpenDropdownKey((currentKey) =>
                        currentKey === dropdownKey ? null : dropdownKey
                      )
                    }
                    style={{
                      width: "100%",
                      color:
                        isDropdownActive || isDropdownOpen
                          ? activeNavUnderlineColor
                          : navTextColor,
                      background: "transparent",
                      border: "none",
                      borderBottom:
                        isDropdownActive || isDropdownOpen
                          ? `2px solid ${activeNavUnderlineColor}`
                          : "2px solid transparent",
                      textDecoration: "none",
                      fontWeight: "700",
                      fontSize: "16px",
                      padding: "0.5rem 0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <span>{link.label}</span>
                    <span
                      aria-hidden="true"
                      style={{
                        fontSize: "12px",
                        transform: isDropdownOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    >
                      â–¾
                    </span>
                  </button>

                  {isDropdownOpen ? (
                    <div
                      id={`mobile-dropdown-${index}`}
                      style={{
                        display: "grid",
                        gap: "0.65rem",
                        padding: "0.85rem 0 0.35rem 0.75rem",
                        border: "1px solid rgba(25, 73, 122, 0.1)",
                        borderRadius: "8px",
                        marginTop: "0.45rem",
                      }}
                    >
                      {link.dropdown.map((dropdownItem) => (
                        <Link
                          key={`${dropdownKey}-${dropdownItem.label}`}
                          href={dropdownItem.href}
                          onClick={() => {
                            closeShopMenu();
                            setIsMobileMenuOpen(false);
                          }}
                          style={{
                            color: isLinkActive(dropdownItem.href)
                              ? activeNavUnderlineColor
                              : navTextColor,
                            textDecoration: "none",
                            fontSize: "15px",
                            fontWeight: 650,
                            padding: "0.1rem 0",
                            textAlign: "left",
                          }}
                        >
                          {dropdownItem.label}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            }

            return (
              <Link
                key={index}
                href={link.href}
                onClick={() => {
                  closeShopMenu();
                  setIsMobileMenuOpen(false);
                }}
                style={{
                  color: isLinkActive(link.href)
                    ? activeNavUnderlineColor
                    : navTextColor,
                  textDecoration: "none",
                  fontWeight: "700",
                  fontSize: "16px",
                  padding: "0.5rem 0",
                  borderBottom: isLinkActive(link.href)
                    ? `2px solid ${activeNavUnderlineColor}`
                    : "2px solid transparent",
                }}
              >
                {link.label}
              </Link>
            );
          })}
          {isLoggedIn && isEcommerceEnabled ? (
            <Link
              href="/wishlist"
              onClick={() => setIsMobileMenuOpen(false)}
              style={{
                color: navTextColor,
                textDecoration: "none",
                fontWeight: "700",
                fontSize: "16px",
                padding: "0.5rem 0",
              }}
            >
              WISHLIST
            </Link>
          ) : null}
          {isLoggedIn && isEcommerceEnabled ? (
            <Link
              href="/user/account"
              onClick={() => setIsMobileMenuOpen(false)}
              style={{
                color: navTextColor,
                textDecoration: "none",
                fontWeight: "700",
                fontSize: "16px",
                padding: "0.5rem 0",
              }}
            >
              ACCOUNT
            </Link>
          ) : null}
        </div>
      )}

        </nav>
      )}

      <CartPopupDrawer
        open={isCartPopupOpen}
        onClose={() => setIsCartPopupOpen(false)}
        theme={theme}
      />

      {/* OTP Modal */}
      {isOtpModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "1rem",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 420,
              background: otpModalTheme.modalBg,
              borderRadius: "18px",
              padding: "1.25rem",
              boxShadow: "0 18px 45px rgba(0,0,0,0.28)",
              border: `1px solid ${otpModalTheme.border}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "0.75rem",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "1.1rem",
                  color: otpModalTheme.heading,
                }}
              >
                Login
              </h3>
              <button
                type="button"
                onClick={handleCloseOtpModal}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: "1.25rem",
                  lineHeight: 1,
                  cursor: "pointer",
                  color: otpModalTheme.heading,
                }}
                aria-label="Close OTP popup"
              >
                ×
              </button>
            </div>

            {console.log("companyDetails", companyDetails)}
            {companyDetails?.tenantOauth != null && (
              <>
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: `1px solid ${otpModalTheme.border}`,
                    borderRadius: "12px",
                    background: otpModalTheme.secondaryBtnBg,
                    color: otpModalTheme.secondaryBtnText,
                    fontWeight: 600,
                    cursor: "pointer",
                    marginBottom: "0.75rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                  }}
                >
                  <Image
                    src={googleIcon}
                    alt="Google"
                    width={20}
                    height={20}
                  />
                  Continue with Google
                </button>

                <button
                  type="button"
                  onClick={() => setIsEmailLoginExpanded((prev) => !prev)}
                  aria-expanded={isEmailLoginExpanded}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    border: "none",
                    background: "transparent",
                    color: "#2563eb",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    textDecoration: "underline",
                    textUnderlineOffset: "3px",
                    padding: "0.2rem 0 0.25rem",
                    marginTop: "1.1rem",
                    marginBottom: "1rem",
                  }}
                >
                  {isEmailLoginExpanded ? "Show less" : "Show more"}
                  <span
                    aria-hidden="true"
                    style={{
                      width: "7px",
                      height: "7px",
                      borderRight: "2px solid currentColor",
                      borderBottom: "2px solid currentColor",
                      transform: isEmailLoginExpanded
                        ? "rotate(225deg)"
                        : "rotate(45deg)",
                      marginTop: isEmailLoginExpanded ? "4px" : "-2px",
                      transition: "transform 0.2s ease, margin 0.2s ease",
                    }}
                  />
                </button>
              </>
            )}

            {(companyDetails?.tenantOauth == null || isEmailLoginExpanded) && (
              <>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.85rem",
                    color: otpModalTheme.text,
                    marginBottom: "0.35rem",
                  }}
                >
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  style={{
                    width: "100%",
                    padding: "0.65rem 0.75rem",
                    borderRadius: "12px",
                    border: `1px solid ${otpModalTheme.border}`,
                    background: otpModalTheme.inputBg,
                    color: otpModalTheme.inputText,
                    marginBottom: "0.75rem",
                  }}
                />

                <button
                  type="button"
                  onClick={isOtpSent ? handleReSendOtp : handleSendOtp}
                  disabled={
                    tenantLoading ||
                    !tenantName ||
                    otpSendOrResendLoading ||
                    (isOtpSent && countdown > 0)
                  }
                  style={{
                    width: "100%",
                    padding: "0.65rem 0.75rem",
                    border: "none",
                    borderRadius: "12px",
                    background: otpModalTheme.primaryBtnBg,
                    color: otpModalTheme.primaryBtnText,
                    fontWeight: 600,
                    cursor:
                      tenantLoading ||
                      !tenantName ||
                      otpSendOrResendLoading ||
                      (isOtpSent && countdown > 0)
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      tenantLoading ||
                      !tenantName ||
                      otpSendOrResendLoading ||
                      (isOtpSent && countdown > 0)
                        ? 0.7
                        : 1,
                    marginBottom: "0.9rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                  }}
                >
                  {tenantLoading ? (
                    <span>Loading...</span>
                  ) : otpSendOrResendLoading ? (
                    <>
                      <Spinner size={16} />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <span>
                      {isOtpSent
                        ? countdown > 0
                          ? "OTP Sent"
                          : "Resend OTP"
                        : "Send OTP"}
                    </span>
                  )}
                </button>

                {isOtpSent && (
                  <>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.85rem",
                        color: otpModalTheme.text,
                        marginBottom: "0.35rem",
                      }}
                    >
                      Enter OTP
                    </label>
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                      placeholder="Enter OTP"
                      maxLength={6}
                      style={{
                        width: "100%",
                        padding: "0.65rem 0.75rem",
                        borderRadius: "12px",
                        border: `1px solid ${otpModalTheme.border}`,
                        background: otpModalTheme.inputBg,
                        color: otpModalTheme.inputText,
                        marginBottom: "0.75rem",
                      }}
                    />

                    <button
                      type="button"
                      onClick={handleVerifyOtp}
                      disabled={otpVerifyButtonLoading}
                      style={{
                        width: "100%",
                        padding: "0.65rem 0.75rem",
                        border: "none",
                        borderRadius: "12px",
                        background: otpModalTheme.secondaryBtnBg,
                        color: otpModalTheme.secondaryBtnText,
                        fontWeight: 600,
                        cursor: otpVerifyButtonLoading ? "not-allowed" : "pointer",
                        opacity: otpVerifyButtonLoading ? 0.7 : 1,
                        marginBottom: "0.6rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "10px",
                      }}
                    >
                      {otpVerifyButtonLoading ? (
                        <>
                          <Spinner size={16} />
                          <span>Verifying...</span>
                        </>
                      ) : (
                        "Verify OTP"
                      )}
                    </button>

                    <p
                      style={{
                        margin: 0,
                        fontSize: "0.82rem",
                        color: otpModalTheme.subtext,
                      }}
                    >
                      {countdown > 0
                        ? `Resend OTP in ${countdown}s`
                        : "You can resend OTP now."}
                    </p>
                  </>
                )}
              </>
            )}

            {/* status message */}
            {otpMessage && (
              <p
                style={{
                  margin: "0.8rem 0 0",
                  fontSize: "0.85rem",
                  color: otpMessage.toLowerCase().includes("success")
                    ? "#1b7f3a"
                    : "#c3382b",
                }}
              >
                {otpMessage}
              </p>
            )}
          </Box>
        </div>
      )}

      <style jsx>{`
        .nav-link {
          transition: color 0.3s ease;
        }
        .nav-link:hover {
          color: ${navbarTheme.hoverColor} !important;
        }
        input::placeholder {
          color: ${searchInputStyles.inputPlaceholder};
          opacity: 1;
        }
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </>
  );
}

